import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import DropdownCombobox from 'common/ui/DropdownCombobox';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import styled, { css } from 'styled-components';
import { Colors, TextCss } from 'styles';
import Fieldset from 'common/ui/Fieldset';
import { GET_STUDY_DESCRIPTIONS } from 'modules/Apollo/queries';
import { EMPTY } from 'config/constants';
import type {
  GetStudyDescriptionsQuery,
  GetStudyDescriptionsQueryVariables,
} from 'generated/graphql';

import { transparentize } from 'color2k';
import { useDebouncedState } from 'hooks/useDebouncedState';
import useMountedState from 'react-use/esm/useMountedState';
import { useSelectedStudyDescriptions } from '../hooks/useSelectedStudyDescriptions';
import { telemetryContextFor } from 'modules/Apollo/types';
import { rootTheme } from 'theme/rootTheme';

type AddRemoveIconProps = {
  action: 'add' | 'remove';
  onClick?: () => void;
  testId?: string;
};

const AddRemoveIcon = styled(({ action, onClick, testId, ...props }: AddRemoveIconProps) => {
  const Icon = action === 'add' ? AddCircleIcon : RemoveCircleIcon;

  return <Icon onClick={onClick} data-testid={testId} {...props} />;
})`
  ${TextCss.body1}
  color: ${(props: AddRemoveIconProps) => (props.action === 'add' ? Colors.green6 : Colors.red6)};
  margin-right: 10px;
  transition: color 0.2s ease-in-out;
  ${(props: AddRemoveIconProps) =>
    props.onClick != null &&
    css`
      &:hover,
      &:focus {
        color: ${props.action === 'add' ? Colors.green5 : Colors.red5};
      }
    `}
`;

type SelectedItemRowProps = {
  label: string;
  templateId: Nullable<string>;
  onRemove: () => void;
  itemIndex: number;
};

const RowCss = `
        all: unset;
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        flex: 1;
        padding: 5px 15px;
      `;

const SelectedItemRow = ({ label, templateId, itemIndex, onRemove }: SelectedItemRowProps) => {
  return (
    <li css={RowCss}>
      <AddRemoveIcon
        action="remove"
        onClick={onRemove}
        css="cursor: pointer;"
        testId={`remove-study-description-${itemIndex}`}
      />
      <span>{label}</span>
    </li>
  );
};

type Props = {
  templateId?: string;
  ['data-testid']?: string;
  css?: string;
};

const StudyDescriptions = ({
  templateId,
  'data-testid': dataTestid,
}: Props): React.ReactElement => {
  const [selectedStudyDescriptions, setStudyDescriptions] = useSelectedStudyDescriptions();
  const [search, debouncedSearch, setSearch] = useDebouncedState('', 500);
  const { data, loading, error, fetchMore } = useQuery<
    GetStudyDescriptionsQuery,
    GetStudyDescriptionsQueryVariables
  >(GET_STUDY_DESCRIPTIONS, {
    variables: { search: debouncedSearch },
    context: APOLLO_QUERY_CONTEXT,
  });
  const studyDescriptions = data?.studyDescriptions?.items || EMPTY.ARRAY;
  const totalCount = data?.studyDescriptions.totalCount || 0;
  const nextCursor = data?.studyDescriptions.next;
  const comboboxItems = useMemo(
    () =>
      studyDescriptions.filter(
        (item) => !selectedStudyDescriptions.find((x) => x === item.description)
      ),
    [studyDescriptions, selectedStudyDescriptions]
  );
  const isMounted = useMountedState();

  return (
    <Fieldset css="flex: 0 1 auto; min-height: 0;">
      <>
        <DropdownCombobox
          // @ts-expect-error [EN-7967] - TS2322 - Type '{ inputValue: string; itemToString: (item: any) => any; placeholder: string; items: any[]; multiple: true; renderItem: (item: any) => Element; onSelectedItemChange: (changes: Change<any>) => void; ... 7 more ...; onLoadMore: () => void; }' is not assignable to type 'IntrinsicAttributes & DropdownComboboxProps<any>'.
          inputValue={search}
          itemToString={(item) => item?.description}
          placeholder="Search for study description..."
          items={comboboxItems}
          multiple
          renderItem={(item) => (
            <div css="display: flex; align-items: center;">
              <AddRemoveIcon
                action={
                  selectedStudyDescriptions.find((x) => x === item.description) ? 'remove' : 'add'
                }
              />
              {item.description}
            </div>
          )}
          onSelectedItemChange={(changes) => {
            if (changes.selectedItem != null) {
              setStudyDescriptions([
                ...selectedStudyDescriptions,
                changes.selectedItem.description,
              ]);
            }
          }}
          onFilter={(inputValue) => (item: any) =>
            item.description.toLowerCase().includes(inputValue.toLowerCase())
          }
          disabled={loading || error}
          data-testid={dataTestid}
          totalCount={totalCount}
          onInputValueChange={(change) => setSearch(change.inputValue)}
          hasNextPage={nextCursor != null}
          loading={loading}
          onLoadMore={() => {
            if (data == null || !isMounted() || nextCursor == null || loading) return;

            fetchMore({
              query: GET_STUDY_DESCRIPTIONS,
              variables: { cursor: nextCursor, search },
            });
          }}
        />

        <ul
          css={`
            padding: 10px 0;
            list-style: none;
            background-color: ${Colors.gray1};
            min-height: 150px;
            overflow: auto;
            margin: 0 -15px;
            margin-top: 20px;
            border-radius: ${rootTheme.radius.md}px;
            border: 1px solid ${transparentize(Colors.white, 0.92)};
          `}
        >
          <li css={RowCss}>Study description</li>
          {selectedStudyDescriptions.map((studyDescription, index) => (
            <SelectedItemRow
              key={studyDescription ?? index}
              templateId={templateId}
              label={studyDescription}
              itemIndex={index}
              onRemove={() =>
                setStudyDescriptions((descriptions) =>
                  descriptions.filter((x) => x !== studyDescription)
                )
              }
            />
          ))}
        </ul>
      </>
    </Fieldset>
  );
};

export default StudyDescriptions;

StudyDescriptions.displayName = 'StudyDescriptions';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(StudyDescriptions);
